/**
 * Calls the standalone Notification.API (not Chemplast WebAPI).
 * All requests are authenticated with the MSAL access token; the API
 * derives the current user (Entra oid) from the JWT — no client-supplied userId.
 */
import { configService } from '../../config/config-service';
import { getMsalAccessToken } from '../../auth/msal';

function getNotificationBaseUrl() {
  const url = configService.notificationApiUrl || 'https://localhost:54191';
  return url.replace(/\/$/, '');
}

function appendInboxFilterParams(params, options = {}) {
  if (options.filter) params.set('filter', options.filter);
  if (options.search) params.set('search', options.search);
  if (options.category) params.set('category', options.category);
  if (options.createdBy) params.set('createdBy', options.createdBy);
  if (options.fromDate) params.set('fromDate', options.fromDate);
  if (options.toDate) params.set('toDate', options.toDate);
  if (options.channel) params.set('channel', options.channel);
  if (options.module) params.set('module', options.module);
  if (options.menu) params.set('menu', options.menu);
  if (options.pageCode) params.set('pageCode', options.pageCode);
  return params;
}

async function getAuthHeaders(extra = {}) {
  const headers = { Accept: 'application/json', ...extra };
  const token = await getMsalAccessToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}

async function handleJsonResponse(response) {
  if (response.status === 204) {
    return null;
  }
  if (!response.ok) {
    let message = `Notification API error ${response.status}`;
    try {
      const body = await response.json();
      message = body.error || body.message || message;
    } catch {
      // ignore
    }
    throw new Error(message);
  }
  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    return response.json();
  }
  return null;
}

/**
 * @param {{
 *   mode?: 'my' | 'recent',
 *   page?: number,
 *   pageSize?: number,
 *   filter?: 'all' | 'unread' | 'needsAck' | 'acknowledged',
 *   search?: string,
 *   category?: string,
 *   createdBy?: string,
 *   fromDate?: string,
 *   toDate?: string,
 *   channel?: 'push' | 'mobile' | 'inApp' | 'web',
 *   module?: string,
 *   menu?: string,
 *   pageCode?: string,
 *   take?: number,
 * }} options
 * @returns {Promise<{ items: object[], page: number, pageSize: number, totalCount: number, unreadCount: number, filteredUnreadCount: number, needsAcknowledgementCount: number }>}
 */
export async function fetchNotifications(options = {}) {
  const mode = options.mode || configService.notificationInboxMode || 'my';
  const base = getNotificationBaseUrl();

  if (mode === 'recent') {
    const take = options.take || options.pageSize || 50;
    const response = await fetch(`${base}/api/v1/notifications/recent?take=${take}`, {
      method: 'GET',
      headers: await getAuthHeaders(),
    });
    const data = await handleJsonResponse(response);
    const items = Array.isArray(data) ? data : [];
    return {
      items,
      page: 1,
      pageSize: items.length,
      totalCount: items.length,
      unreadCount: items.filter(n => !n.isRead).length,
      filteredUnreadCount: items.filter(n => !n.isRead).length,
      needsAcknowledgementCount: items.filter(n => n.requiresAck && !n.isAcknowledged).length,
    };
  }

  const params = new URLSearchParams();
  params.set('page', String(options.page || 1));
  params.set('pageSize', String(options.pageSize || 20));
  appendInboxFilterParams(params, { ...options, filter: options.filter || 'all' });

  const response = await fetch(`${base}/api/v1/notifications/my?${params.toString()}`, {
    method: 'GET',
    headers: await getAuthHeaders(),
  });
  const data = await handleJsonResponse(response);

  // Support both paged object and legacy array.
  if (Array.isArray(data)) {
    return {
      items: data,
      page: 1,
      pageSize: data.length,
      totalCount: data.length,
      unreadCount: data.filter(n => !n.isRead).length,
      filteredUnreadCount: data.filter(n => !n.isRead).length,
      needsAcknowledgementCount: data.filter(n => n.requiresAck && !n.isAcknowledged).length,
    };
  }

  return {
    items: Array.isArray(data?.items) ? data.items : [],
    page: data?.page || 1,
    pageSize: data?.pageSize || options.pageSize || 20,
    totalCount: data?.totalCount || 0,
    unreadCount: data?.unreadCount || 0,
    filteredUnreadCount: data?.filteredUnreadCount ?? data?.unreadCount ?? 0,
    needsAcknowledgementCount: data?.needsAcknowledgementCount || 0,
  };
}

export async function markNotificationRead(notificationId) {
  const base = getNotificationBaseUrl();
  const response = await fetch(`${base}/api/v1/notifications/read`, {
    method: 'POST',
    headers: await getAuthHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ notificationId }),
  });
  return handleJsonResponse(response);
}

/**
 * @param {{
 *   filter?: 'all' | 'unread' | 'needsAck' | 'acknowledged',
 *   search?: string,
 *   category?: string,
 *   createdBy?: string,
 *   fromDate?: string,
 *   toDate?: string,
 *   channel?: 'push' | 'mobile' | 'inApp' | 'web',
 *   module?: string,
 *   menu?: string,
 *   pageCode?: string,
 * }} [options] Inbox filters. Omit to mark the entire inbox (header bell).
 * @returns {Promise<{ updatedCount?: number } | null>}
 */
export async function markAllNotificationsRead(options = {}) {
  const base = getNotificationBaseUrl();
  const params = appendInboxFilterParams(new URLSearchParams(), options);
  const qs = params.toString();
  const response = await fetch(
    `${base}/api/v1/notifications/read-all${qs ? `?${qs}` : ''}`,
    {
      method: 'POST',
      headers: await getAuthHeaders({ 'Content-Type': 'application/json' }),
    },
  );
  return handleJsonResponse(response);
}

export async function acknowledgeNotification(notificationId) {
  const base = getNotificationBaseUrl();
  const response = await fetch(`${base}/api/v1/notifications/acknowledge`, {
    method: 'POST',
    headers: await getAuthHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ notificationId }),
  });
  return handleJsonResponse(response);
}

/** @returns {Promise<object[]>} */
export async function fetchNotificationEvents() {
  const base = getNotificationBaseUrl();
  const response = await fetch(`${base}/api/v1/events`, {
    method: 'GET',
    headers: await getAuthHeaders(),
  });
  const data = await handleJsonResponse(response);
  return Array.isArray(data) ? data : [];
}

/**
 * @param {string} eventId
 * @param {{
 *   displayName: string,
 *   category: string,
 *   description?: string | null,
 *   defaultPriority: number,
 *   interactionType: number,
 *   requiresAck?: boolean,
 *   channelInApp?: boolean,
 *   channelEmail?: boolean,
   *   channelSms?: boolean,
   *   channelPush?: boolean,
   *   pushModuleId?: number | null,
   *   pushMenuId?: number | null,
   *   pushPageId?: number | null,
   *   messageTemplate?: string | null,
   *   emailSubjectTemplate?: string | null,
   *   emailBodyTemplate?: string | null,
   *   emailUrl?: string | null,
   *   emailToList?: string | null,
   *   isActive: boolean,
   * }} payload
 */
export async function updateNotificationEvent(eventId, payload) {
  const base = getNotificationBaseUrl();
  const response = await fetch(`${base}/api/v1/events/${eventId}`, {
    method: 'PUT',
    headers: await getAuthHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(payload),
  });
  return handleJsonResponse(response);
}

/** Active mobile modules and pages for Events Master push targeting. */
export async function fetchMobileCatalog() {
  const base = getNotificationBaseUrl();
  const response = await fetch(`${base}/api/v1/events/mobile-catalog`, {
    method: 'GET',
    headers: await getAuthHeaders(),
  });
  const data = await handleJsonResponse(response);
  return {
    modules: Array.isArray(data?.modules) ? data.modules : [],
  };
}

/**
 * Role routing for one event (in-app recipients) plus the category → user-group catalog.
 * @param {string} eventId
 * @returns {Promise<{
 *   eventId: string,
 *   eventCode: string,
 *   eventCategory: string,
 *   roles: string[],
 *   hasFromPayload: boolean,
 *   hasExplicitUsers: boolean,
 *   roleGroups: Array<{ category: string, roles: string[] }>,
 * }>}
 */
export async function fetchEventRouting(eventId) {
  const base = getNotificationBaseUrl();
  const response = await fetch(`${base}/api/v1/events/${encodeURIComponent(eventId)}/routing`, {
    method: 'GET',
    headers: await getAuthHeaders(),
  });
  return handleJsonResponse(response);
}

/**
 * Replaces Role routing for an event. FromPayload / ExplicitUser rules are unchanged.
 * @param {string} eventId
 * @param {string[]} roles
 */
export async function updateEventRouting(eventId, roles) {
  const base = getNotificationBaseUrl();
  const response = await fetch(`${base}/api/v1/events/${encodeURIComponent(eventId)}/routing`, {
    method: 'PUT',
    headers: await getAuthHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ roles }),
  });
  return handleJsonResponse(response);
}

/**
 * App roles from Notification_UserRoleSnapshot (DB; no live Graph).
 * @returns {Promise<Array<{ displayName?: string, value?: string }>>}
 */
export async function fetchNotificationAppRoles() {
  const base = getNotificationBaseUrl();
  const response = await fetch(`${base}/api/v1/sync/snapshot/roles`, {
    method: 'GET',
    headers: await getAuthHeaders(),
  });
  const data = await handleJsonResponse(response);
  return Array.isArray(data?.items) ? data.items : [];
}

/**
 * Role assignments (user + email) from Notification_UserRoleSnapshot (DB; no live Graph).
 * @param {string} [role]
 * @returns {Promise<Array<{ roleName?: string, userId?: string, userDisplayName?: string, userEmail?: string }>>}
 */
export async function fetchNotificationRoleAssignments(role) {
  const base = getNotificationBaseUrl();
  const params = new URLSearchParams();
  if (role) params.set('role', String(role).trim());
  const qs = params.toString();
  const response = await fetch(
    `${base}/api/v1/sync/snapshot/role-assignments${qs ? `?${qs}` : ''}`,
    {
      method: 'GET',
      headers: await getAuthHeaders(),
    },
  );
  const data = await handleJsonResponse(response);
  return Array.isArray(data?.items) ? data.items : [];
}

function parseNotificationPayload(value) {
  if (!value) return null;
  if (typeof value === 'object') return value;
  if (typeof value !== 'string') return null;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

function getNotificationEventCode(item) {
  return String(
    item?.eventCode || item?.EventCode || item?.eventName || item?.title || '',
  ).trim();
}

function isAcceptRejectEntryNotification(item, payload) {
  const code = getNotificationEventCode(item).toLowerCase();
  if (
    code === 'acceptrejectentrycreated' ||
    code === 'acceptrejectevidencerequested' ||
    code.includes('accept or reject') ||
    code.includes('more evidence requested')
  ) {
    return true;
  }
  if (payload?.requestId || payload?.RequestId) {
    if (payload?.inspectionResultId || payload?.alertId || payload?.resultId) {
      return false;
    }
    return Boolean(payload?.partId || payload?.PartId || payload?.partNumber || payload?.PartNumber);
  }
  return false;
}

function isQualityAlertAddedNotification(item, payload) {
  const code = getNotificationEventCode(item).toLowerCase();
  if (code === 'qualityalertadded' || code.includes('quality alert added')) {
    return true;
  }
  if (payload?.alertId || payload?.AlertId) {
    return Boolean(payload?.partId || payload?.PartId);
  }
  return false;
}

function buildQualityAlertLinkUrl(item, payload) {
  const message = String(item?.message || '');
  let partId = payload?.partId ?? payload?.PartId;
  const alertId = payload?.alertId ?? payload?.AlertId;
  if (!partId) {
    const match = message.match(/for part\s+(\d+)/i);
    if (match) partId = match[1];
  }
  const params = new URLSearchParams();
  if (partId) params.set('partId', String(partId));
  if (alertId) params.set('alertId', String(alertId));
  const query = params.toString();
  return query ? `/quality-alert-pages?${query}` : '/quality-alert-pages';
}

function buildInspectionPlanLinkUrl(item, payload) {
  const message = String(item?.message || '');
  const inspectionPlanId =
    payload?.inspectionPlanId ?? payload?.InspectionPlanId ?? item?.entityId;
  let partId = payload?.partId ?? payload?.PartId;
  let partNumber = payload?.partNumber ?? payload?.PartNumber ?? '';
  if (!partNumber) {
    const match = message.match(/for part\s+([A-Za-z0-9._-]+)/i);
    if (match) partNumber = match[1];
  }
  const params = new URLSearchParams();
  if (inspectionPlanId) params.set('inspectionPlanId', String(inspectionPlanId));
  if (partId) params.set('partId', String(partId));
  if (partNumber) params.set('partNumber', String(partNumber));
  const query = params.toString();
  return query ? `/inspectionplan-tile?${query}` : '/inspectionplan-tile';
}

function buildChangeRequestLinkUrl(item, payload) {
  const changeRequestId =
    payload?.changeRequestId ?? payload?.ChangeRequestId ?? item?.entityId;
  if (changeRequestId && /^\d+$/.test(String(changeRequestId))) {
    return `/change-request?changeRequestId=${changeRequestId}`;
  }
  const stored = item?.linkUrl;
  if (stored && /\/change-request\/(\d+)/.test(stored)) {
    return `/change-request?changeRequestId=${stored.match(/\/change-request\/(\d+)/)[1]}`;
  }
  return stored || '/change-request';
}

function buildReceivingVoidLinkUrl(item, payload) {
  const shipmentId =
    payload?.shipmentId ?? payload?.ShipmentID ?? payload?.ShipmentId ?? item?.entityId;
  if (shipmentId) {
    return `/mappings?shipmentId=${shipmentId}`;
  }
  return item?.linkUrl || '/mappings';
}

function buildWorkTicketLinkUrl(item, payload) {
  const processId = payload?.processId ?? payload?.ProcessId ?? item?.entityId;
  const workTicket =
    payload?.workTicketNumber ?? payload?.WorkTicketNumber ?? payload?.workTicket ?? '';
  const params = new URLSearchParams();
  if (processId) params.set('processId', String(processId));
  if (workTicket) params.set('workTicket', String(workTicket));
  const query = params.toString();
  return query ? `/plannerv2?${query}` : '/plannerv2';
}

function buildMoldReceivingLinkUrl(payload) {
  const params = new URLSearchParams();
  if (payload?.toolId ?? payload?.ToolID) {
    params.set('toolId', String(payload?.toolId ?? payload?.ToolID));
  }
  if (payload?.moldInfoId ?? payload?.MoldInfoID) {
    params.set('moldInfoId', String(payload?.moldInfoId ?? payload?.MoldInfoID));
  }
  const query = params.toString();
  return query ? `/mold-receiving?${query}` : '/mold-receiving';
}

function buildMoldReleaseLinkUrl(payload) {
  const params = new URLSearchParams();
  if (payload?.toolId ?? payload?.ToolID) {
    params.set('toolId', String(payload?.toolId ?? payload?.ToolID));
  }
  if (payload?.moldInfoId ?? payload?.MoldInfoID) {
    params.set('moldInfoId', String(payload?.moldInfoId ?? payload?.MoldInfoID));
  }
  const query = params.toString();
  return query ? `/mold-release?${query}` : '/mold-release';
}

function buildAcceptRejectLinkUrl(item, payload) {
  const message = String(item?.message || '');
  const partId = payload?.partId ?? payload?.PartId;
  const requestId = payload?.requestId ?? payload?.RequestId ?? item?.entityId;
  let partNumber = payload?.partNumber ?? payload?.PartNumber ?? '';
  let workTicket = payload?.workTicket ?? payload?.WorkTicket ?? '';
  if (!partNumber) {
    const match = message.match(/part\s+([A-Za-z0-9._-]+)/i);
    if (match) partNumber = match[1];
  }
  if (!workTicket) {
    const match = message.match(/work ticket\s+([A-Za-z0-9._-]+)/i);
    if (match) workTicket = match[1];
  }
  const params = new URLSearchParams();
  if (partId) params.set('partId', String(partId));
  if (requestId) params.set('requestId', String(requestId));
  if (partNumber) params.set('partNumber', String(partNumber));
  if (workTicket) params.set('workTicket', String(workTicket));
  const query = params.toString();
  return query ? `/quality-approval?${query}` : '/quality-approval';
}

/**
 * Prefer a result-level Inspection Results deep link when the event payload
 * has part/result ids. Rebuild MMS record links from payload so older inbox
 * items still open the specific record. Falls back to the stored linkUrl.
 */
export function resolveNotificationLinkUrl(item) {
  if (!item) return undefined;
  const payload = parseNotificationPayload(item.payload ?? item.Payload);
  const eventCode = getNotificationEventCode(item);
  const partId = payload?.partId;
  const resultId = payload?.inspectionResultId ?? payload?.resultId;
  const tab =
    eventCode === 'AqlInspectionCompleted'
      ? 'aql'
      : eventCode === 'ReleaseInspectionCompleted'
        ? 'release'
        : null;

  if (tab && partId) {
    const params = new URLSearchParams();
    params.set('partId', String(partId));
    if (resultId) params.set('resultId', String(resultId));
    params.set('tab', tab);
    if (payload?.workTicketNumber) {
      params.set('workTicket', String(payload.workTicketNumber));
    }
    return `/inspection-results?${params.toString()}`;
  }

  if (eventCode === 'MachineMaintenanceAdded' || eventCode === 'MachineMaintenanceUpdated') {
    const params = new URLSearchParams();
    if (payload?.machineId) params.set('machineId', String(payload.machineId));
    if (payload?.maintenanceId) params.set('maintenanceId', String(payload.maintenanceId));
    if (payload?.workOrderNumber) params.set('workOrderNumber', String(payload.workOrderNumber));
    const query = params.toString();
    if (query) {
      return `/cell?${query}`;
    }
  }

  if (
    eventCode === 'MoldMaintenanceAdded' ||
    eventCode === 'MoldMaintenanceUpdated' ||
    eventCode === 'MoldMaintenanceClosed'
  ) {
    const params = new URLSearchParams();
    if (payload?.toolId) params.set('toolId', String(payload.toolId));
    if (payload?.maintenanceId) params.set('maintenanceId', String(payload.maintenanceId));
    if (payload?.moldInfoId) params.set('moldInfoId', String(payload.moldInfoId));
    const query = params.toString();
    if (query) {
      return `/mold-maintenance?${query}`;
    }
  }

  if (eventCode === 'MoldFutureActionAdded' || eventCode === 'MoldFutureActionUpdated') {
    const params = new URLSearchParams();
    if (payload?.moldInfoId) params.set('moldInfoId', String(payload.moldInfoId));
    if (payload?.futureActionId) params.set('futureActionId', String(payload.futureActionId));
    if (payload?.maintenanceId) params.set('maintenanceId', String(payload.maintenanceId));
    const query = params.toString();
    if (query) {
      return `/future-actions?${query}`;
    }
  }

  if (isAcceptRejectEntryNotification(item, payload)) {
    return buildAcceptRejectLinkUrl(item, payload);
  }

  if (isQualityAlertAddedNotification(item, payload)) {
    return buildQualityAlertLinkUrl(item, payload);
  }

  if (
    eventCode === 'InspectionPlanCreatedOrUpdated' ||
    payload?.inspectionPlanId ||
    String(item?.title || '').toLowerCase().startsWith('inspection plan created') ||
    String(item?.title || '').toLowerCase().startsWith('inspection plan updated')
  ) {
    return buildInspectionPlanLinkUrl(item, payload);
  }

  if (
    eventCode === 'ChangeRequestCreated' ||
    payload?.changeRequestId ||
    String(item?.title || '').toLowerCase() === 'change request created'
  ) {
    return buildChangeRequestLinkUrl(item, payload);
  }

  if (eventCode === 'ReceivingItemVoided') {
    return buildReceivingVoidLinkUrl(item, payload);
  }

  if (eventCode === 'WorkTicketAssignedToCell' || eventCode === 'WorkTicketStatusChanged') {
    return buildWorkTicketLinkUrl(item, payload);
  }

  if (eventCode === 'MoldAdded') {
    return buildMoldReceivingLinkUrl(payload);
  }

  if (eventCode === 'MoldReleased') {
    return buildMoldReleaseLinkUrl(payload);
  }

  if (eventCode === 'ShipmentCreated' || String(item?.title || '').toLowerCase() === 'shipment created') {
    const shipmentId = payload?.shipmentId ?? payload?.ShipId ?? item.entityId;
    const asp = payload?.asp ?? payload?.ASPNum ?? payload?.aspNum ?? '';
    const params = new URLSearchParams();
    if (shipmentId) params.set('shipmentId', String(shipmentId));
    if (asp) params.set('asp', String(asp));
    const query = params.toString();
    if (query) {
      return `/shipment-tile?${query}`;
    }
    return '/shipment-tile';
  }

  return item.linkUrl;
}

/**
 * Notification.API stores CreatedDate in UTC. Older responses omit 'Z', so the
 * browser would treat the value as local time. Always parse naive stamps as UTC.
 */
function parseNotificationUtcDate(isoDate) {
  if (!isoDate) return null;
  if (isoDate instanceof Date) {
    return Number.isNaN(isoDate.getTime()) ? null : isoDate;
  }

  const raw = String(isoDate).trim();
  if (!raw) return null;

  const hasTimezone = /[zZ]$/.test(raw) || /[+-]\d{2}:\d{2}$/.test(raw);
  const iso = hasTimezone ? raw : `${raw.replace(' ', 'T')}Z`;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatNotificationTime(isoDate) {
  const date = parseNotificationUtcDate(isoDate);
  if (!date) return '';
  const diffMs = Date.now() - date.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} min${mins === 1 ? '' : 's'} ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}
